import { computed, ref, ComputedRef, Ref } from "vue";
import dayjs from "dayjs";
import {
  OrderLine,
  OrderLinePublicType,
  OrderSession,
  OrderToken,
  TokenStatus,
} from "@smeetz/order-common";
import type { OrderJson } from "@smeetz/orders";

import { getOrderScanHistory } from "@/api/check-in/check-in";
import {
  IRedeemResult,
  CheckInOutcome,
  IOrderTokenHistory,
  ScanLogAction,
} from "@/types/check-in";
import { IProductCategory, PCTypes } from "@/types/db/product-category";
import { LogService } from "@/services/logging";
import { ACCESS_TICKET_TYPES } from "@/constants/check-in";
import { getProductCategoriesByUuids } from "@/dexie/product-category";
import POSConfigModule from "@/store/modules/pos-config";
import { readOrderForCheckIn } from "@/utils/vue-helpers/orders.helper";
import { pullMissingProductCategories } from "@/utils/vue-helpers/orders";
import {
  getHttpStatus,
  withNotSyncedRetry,
} from "@/utils/helpers/not-synced-retry";

// The catalog keeps the type of a category as the name of its PCTypes value.
const CATALOG_ACCESS_TYPES = new Set<string>(
  ACCESS_TICKET_TYPES.map((type) => PCTypes[type])
);

// Line types that give access, for a line whose category is not in the catalog.
const LINE_ACCESS_TYPES = new Set<OrderLinePublicType>([
  OrderLinePublicType.TICKET,
  OrderLinePublicType.SEATED,
  OrderLinePublicType.MEMBERSHIP,
  OrderLinePublicType.MEMBERSHIP_SEATED,
]);

// The engine opens a scan this long before the slot starts when the category
// says nothing. Keep it in step with the access control service.
const DEFAULT_SCAN_OPENING_MINUTES = 15;

// A sold order reaches the engine about a minute after the sale. The page
// asks again on this interval, for about two minutes.
const SYNC_POLL_DELAY_MS = 3000;
const SYNC_POLL_RETRIES = 40;

/** Why a line cannot go to the engine right now. */
export enum LineTiming {
  Now = "now",
  NotOpenYet = "not-open-yet",
  AnotherDay = "another-day",
  Ended = "ended",
}

export interface ICheckInTicket {
  /** The token id. */
  id: string;
  token: string;
  categoryId: string;
  categoryName: string;
  priceId: string;
  priceName: string;
  timeSlotId: number | null;
  startDateTime: string | null;
  endDateTime: string | null;
  seat: string;
  isCheckedIn: boolean;
  /** When the ticket last came in, from the engine log. */
  lastScanAt: string | null;
  /** Who let it in. */
  lastScanBy: string;
}

export interface ICheckInLine {
  key: string;
  categoryName: string;
  priceName: string;
  startDateTime: string | null;
  endDateTime: string | null;
  tickets: ICheckInTicket[];
  total: number;
  checkedIn: number;
  /** Tickets that the operator can still check in on this line. */
  open: number;
  isEligibleNow: boolean;
  timing: LineTiming;
  /** When the scan opens, for a line that is not open yet. */
  opensAt: string | null;
}

/**
 * Say where a ticket stands against its scan window. A ticket with no date
 * is always open, for example a membership. This only splits the list.
 * The engine makes the decision.
 *
 * @param {ICheckInTicket} ticket the ticket to examine.
 * @param {number} openingMinutes how early the scan opens for its category.
 * @returns {{ timing: LineTiming; opensAt: string | null }} where it stands.
 */
function timingOf(
  ticket: ICheckInTicket,
  openingMinutes: number
): { timing: LineTiming; opensAt: string | null } {
  if (!ticket.startDateTime) {
    return { timing: LineTiming.Now, opensAt: null };
  }

  const now = dayjs();
  const start = dayjs(ticket.startDateTime);
  const opensAt = start.subtract(openingMinutes, "minute");
  const end = ticket.endDateTime
    ? dayjs(ticket.endDateTime)
    : start.endOf("day");

  if (now.isAfter(end)) {
    return { timing: LineTiming.Ended, opensAt: null };
  }

  if (now.isBefore(opensAt)) {
    return {
      timing: opensAt.isSame(now, "day")
        ? LineTiming.NotOpenYet
        : LineTiming.AnotherDay,
      opensAt: opensAt.toISOString(),
    };
  }

  return { timing: LineTiming.Now, opensAt: null };
}

/**
 * Say whether a line lets its holder in. The catalog type of its category
 * decides, as for the Orders count; the line type stands in for a category
 * the catalog does not hold.
 *
 * @param {OrderLine} line the line of the ticket.
 * @param {IProductCategory | undefined} category its category in the catalog.
 * @returns {boolean} true when the ticket gives access.
 */
function givesAccess(
  line: OrderLine,
  category: IProductCategory | undefined
): boolean {
  return category
    ? CATALOG_ACCESS_TYPES.has(category.type)
    : LINE_ACCESS_TYPES.has(line.itemType);
}

/**
 * Get the seat of one ticket. The tokens of a line follow its units, and so
 * do its seats.
 *
 * @param {OrderLine} line the line of the ticket.
 * @param {OrderToken} token the ticket.
 * @param {number} position the place of the token among the line's tokens.
 * @returns {string} the seat label, or an empty string.
 */
function seatOf(line: OrderLine, token: OrderToken, position: number): string {
  const unitIndex = token.unitItemId
    ? (line.unitItems ?? []).findIndex((unit) => unit.id === token.unitItemId)
    : -1;
  const seat = line.seats?.[unitIndex >= 0 ? unitIndex : position];

  return seat?.seatLabel || seat?.seatId || "";
}

/**
 * Turn a date of the order into the format the page holds.
 *
 * @param {Date | string | null | undefined} value the date of the line.
 * @returns {string | null} the ISO string, or null.
 */
function toIso(value: Date | string | null | undefined): string | null {
  return value ? dayjs(value).toISOString() : null;
}

/**
 * Get the lines of every session of an order, by id.
 *
 * @param {OrderJson} order the order to read.
 * @returns {Map<string, OrderLine>} the lines.
 */
function linesOf(order: OrderJson): Map<string, OrderLine> {
  const sessions = [order.orderSession, ...(order.previousSessions ?? [])];

  return new Map(
    sessions
      .filter((session): session is OrderSession => !!session)
      .flatMap((session) => session.orderLines ?? [])
      .map((line) => [line.id, line])
  );
}

export interface IRedeemableLinesApi {
  tickets: Ref<ICheckInTicket[]>;
  /** How many tickets the order holds, before the access filter. */
  rawCount: Ref<number>;
  /** Id of the order that was read. */
  orderId: Ref<string | null>;
  isLoading: Ref<boolean>;
  hasError: Ref<boolean>;
  /** True once the engine answered for this order. */
  isSynced: Ref<boolean>;
  /** True while the engine does not know the order yet. */
  isWaitingForSync: Ref<boolean>;
  lines: ComputedRef<ICheckInLine[]>;
  eligibleLines: ComputedRef<ICheckInLine[]>;
  notOpenLines: ComputedRef<ICheckInLine[]>;
  totalCount: ComputedRef<number>;
  checkedInCount: ComputedRef<number>;
  load: (reference: string) => Promise<void>;
  refreshScans: () => Promise<void>;
  applyResults: (results: IRedeemResult[], scannedBy: string) => void;
  stop: () => void;
}

/**
 * Read the tickets of one order, and group the identical ones.
 *
 * @returns {IRedeemableLinesApi} the lines, the counts and the load function.
 */
export function useRedeemableLines(): IRedeemableLinesApi {
  const tickets = ref<ICheckInTicket[]>([]);
  const rawCount = ref(0);
  const orderId = ref<string | null>(null);
  // Category uuid to the minutes the scan opens before the slot starts.
  const scanWindows = ref<Record<string, number>>({});
  const isLoading = ref(false);
  const hasError = ref(false);
  const isSynced = ref(false);
  const isWaitingForSync = ref(false);

  // Each load gets a number. An answer for an older load is dropped, so a
  // page reopened on another order never shows the tickets of the first one.
  let loadGeneration = 0;

  /**
   * Get the group of the POS, which the engine checks against the operator.
   *
   * @returns {number} the group id.
   */
  function groupId(): number {
    return Number(POSConfigModule.groupId);
  }

  /**
   * Get the tickets of an order from its tokens, then wait until the engine
   * knows the order.
   *
   * @param {string} reference the order to read.
   * @returns {Promise<void>}
   */
  async function load(reference: string): Promise<void> {
    const generation = ++loadGeneration;

    isLoading.value = true;
    hasError.value = false;
    isSynced.value = false;
    isWaitingForSync.value = false;
    orderId.value = null;

    let isLocal = false;

    try {
      const read = await readOrderForCheckIn(reference);
      const linesById = linesOf(read.order);
      const tokens = (read.order.tokens ?? []).filter(
        (token) =>
          token.status === TokenStatus.ACTIVE &&
          linesById.has(token.orderLineId)
      );
      const orderLines = [...linesById.values()];

      await pullMissingProductCategories(orderLines);

      const categoryUuids = [
        ...new Set(orderLines.map((line) => String(line.categoryId))),
      ];
      const categories = new Map(
        (await getProductCategoriesByUuids(categoryUuids)).map((category) => [
          category.uuid,
          category,
        ])
      );

      if (generation !== loadGeneration) {
        return;
      }

      // Seats follow the place of a token among all the tokens of its line.
      const positions = new Map<string, number>();
      const allTokens = read.order.tokens ?? [];

      for (const line of linesById.values()) {
        allTokens
          .filter((token) => token.orderLineId === line.id)
          .forEach((token, index) => positions.set(token.id, index));
      }

      const scanTickets: ICheckInTicket[] = [];

      for (const token of tokens) {
        const line = linesById.get(token.orderLineId) as OrderLine;
        const category = categories.get(String(line.categoryId));

        if (!givesAccess(line, category)) {
          continue;
        }

        scanTickets.push({
          id: token.id,
          token: token.token,
          categoryId: String(line.categoryId),
          categoryName: line.categoryName || "",
          priceId: String(line.priceId),
          priceName: line.priceName || "",
          timeSlotId: line.timeSlotId ?? null,
          startDateTime: toIso(line.startDateTime),
          endDateTime: toIso(line.endDateTime),
          seat: seatOf(line, token, positions.get(token.id) ?? 0),
          isCheckedIn: !!token.scannedAt,
          lastScanAt: null,
          lastScanBy: "",
        });
      }

      const windows: Record<string, number> = {};
      for (const ticket of scanTickets) {
        windows[ticket.categoryId] =
          categories.get(ticket.categoryId)?.categoryInfo
            ?.scanOpeningMinutesBeforeStart ?? DEFAULT_SCAN_OPENING_MINUTES;
      }

      rawCount.value = tokens.length;
      tickets.value = scanTickets;
      scanWindows.value = windows;
      orderId.value = read.order.id;
      isLocal = read.isLocal;

      LogService.info("Check-in: tickets read", {
        params: {
          reference,
          orderId: read.order.id,
          inOrder: rawCount.value,
          giveAccess: tickets.value.length,
          fromThisPos: isLocal,
        },
      });
    } catch (error) {
      if (generation !== loadGeneration) {
        return;
      }

      hasError.value = true;
      tickets.value = [];
      isLoading.value = false;
      throw error;
    }

    // The page stays loading until the engine first answers for the order,
    // so the list never shows before the page knows it can act on it.
    await waitUntilSynced(generation, isLocal);
  }

  /**
   * Read who is in, and wait while the engine does not know the order yet.
   * An order sold on this POS can still be on its way to the backend, so a
   * "not found" for it also means "not yet". A request that got no answer
   * says nothing about the order, so the page keeps waiting too.
   *
   * @param {number} generation the load this wait belongs to.
   * @param {boolean} isLocal whether the order is the copy this POS holds.
   * @returns {Promise<void>}
   */
  async function waitUntilSynced(
    generation: number,
    isLocal: boolean
  ): Promise<void> {
    const id = orderId.value;

    if (!id) {
      isLoading.value = false;

      return;
    }

    const isNotSyncedYet = (error: unknown): boolean => {
      const status = getHttpStatus(error);

      return (
        status === undefined || status === 409 || (status === 404 && isLocal)
      );
    };

    try {
      const history = await withNotSyncedRetry(
        () => {
          if (generation !== loadGeneration) {
            throw new Error("Check-in: the read was replaced");
          }

          return getOrderScanHistory(id, groupId());
        },
        (error) => {
          if (generation !== loadGeneration || !isNotSyncedYet(error)) {
            return false;
          }

          isWaitingForSync.value = true;
          isLoading.value = false;

          return true;
        },
        SYNC_POLL_RETRIES,
        SYNC_POLL_DELAY_MS
      );

      if (generation !== loadGeneration) {
        return;
      }

      applyScanHistory(history);
      isSynced.value = true;
      isWaitingForSync.value = false;
      isLoading.value = false;
    } catch (error) {
      if (generation !== loadGeneration) {
        return;
      }

      isLoading.value = false;

      if (isNotSyncedYet(error)) {
        LogService.info("Check-in: the order is still not synced", {
          error,
          params: { orderId: id },
        });

        return;
      }

      // The engine knows the order but refused the read. Each action reports
      // its own error.
      LogService.info("Check-in: could not read the scans of the order", {
        error,
      });
      isSynced.value = true;
      isWaitingForSync.value = false;
    }
  }

  /**
   * Read who is already in from the engine log again.
   *
   * A failure leaves the tickets as they are.
   *
   * @returns {Promise<void>}
   */
  async function refreshScans(): Promise<void> {
    if (!orderId.value) {
      return;
    }

    try {
      applyScanHistory(await getOrderScanHistory(orderId.value, groupId()));
    } catch (error) {
      LogService.info("Check-in: could not read the scans of the order", {
        error,
      });
    }
  }

  /**
   * Mark who is in from the engine log.
   *
   * The engine keeps its own record of every entry, so this holds even when
   * the token was not marked.
   *
   * @param {IOrderTokenHistory[]} history the scans of the order.
   * @returns {void}
   */
  function applyScanHistory(history: IOrderTokenHistory[]): void {
    const byToken = new Map(history.map((entry) => [entry.tokenId, entry]));

    tickets.value = tickets.value.map((ticket) => {
      const entry = byToken.get(ticket.id);

      if (!entry) {
        return ticket;
      }

      const lastScan = [...(entry.scanLogs || [])]
        .reverse()
        .find((log) => log.action === ScanLogAction.Scan);

      return {
        ...ticket,
        isCheckedIn: entry.validScans > 0,
        lastScanAt:
          entry.validScans > 0 && lastScan ? lastScan.createdAt : null,
        lastScanBy:
          entry.validScans > 0 && lastScan?.scanningUser
            ? [lastScan.scanningUser.firstName, lastScan.scanningUser.lastName]
                .filter(Boolean)
                .join(" ")
            : "",
      };
    });
  }

  /**
   * Stop waiting for the order. An answer that comes later is dropped.
   *
   * @returns {void}
   */
  function stop(): void {
    loadGeneration += 1;
    isLoading.value = false;
    isWaitingForSync.value = false;
  }

  // Identical tickets share a category, a slot and a price.
  const lines = computed<ICheckInLine[]>(() => {
    const groups = new Map<string, ICheckInTicket[]>();

    for (const ticket of tickets.value) {
      const key = [
        ticket.categoryId,
        ticket.timeSlotId ?? "none",
        ticket.startDateTime ?? "none",
        ticket.priceId,
      ].join("|");

      const group = groups.get(key);
      if (group) {
        group.push(ticket);
      } else {
        groups.set(key, [ticket]);
      }
    }

    return [...groups.entries()].map(([key, groupTickets]) => {
      const first = groupTickets[0];
      const checkedIn = groupTickets.filter((t) => t.isCheckedIn).length;
      const { timing, opensAt } = timingOf(
        first,
        scanWindows.value[first.categoryId] ?? DEFAULT_SCAN_OPENING_MINUTES
      );

      return {
        key,
        categoryName: first.categoryName,
        priceName: first.priceName,
        startDateTime: first.startDateTime,
        endDateTime: first.endDateTime,
        tickets: groupTickets,
        total: groupTickets.length,
        checkedIn,
        open: groupTickets.length - checkedIn,
        isEligibleNow: timing === LineTiming.Now,
        timing,
        opensAt,
      };
    });
  });

  const eligibleLines = computed(() =>
    lines.value.filter((line) => line.isEligibleNow)
  );

  // Everything the engine will not take right now, with its reason.
  const notOpenLines = computed(() =>
    lines.value.filter((line) => !line.isEligibleNow)
  );

  const totalCount = computed(() => tickets.value.length);

  const checkedInCount = computed(
    () => tickets.value.filter((ticket) => ticket.isCheckedIn).length
  );

  /**
   * Mark the tickets that the engine accepted. This keeps the counts
   * correct without a second read of the order.
   *
   * @param {IRedeemResult[]} results the results of the engine.
   * @param {string} scannedBy the operator who let them in.
   * @returns {void}
   */
  function applyResults(results: IRedeemResult[], scannedBy: string): void {
    const accepted = new Map(
      results
        .filter((result) => result.outcome === CheckInOutcome.Ok)
        .map((result) => [result.tokenId, result.scannedAt])
    );
    const now = new Date().toISOString();

    tickets.value = tickets.value.map((ticket) =>
      accepted.has(ticket.id)
        ? {
            ...ticket,
            isCheckedIn: true,
            lastScanAt: accepted.get(ticket.id) || now,
            lastScanBy: scannedBy,
          }
        : ticket
    );
  }

  return {
    tickets,
    rawCount,
    orderId,
    isLoading,
    hasError,
    isSynced,
    isWaitingForSync,
    lines,
    eligibleLines,
    notOpenLines,
    totalCount,
    checkedInCount,
    load,
    refreshScans,
    applyResults,
    stop,
  };
}
